import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, num } from '../lib/format';
import { downloadXlsx, FMT_COP, fileNameConFecha } from '../lib/xlsx';
import type { Categoria, EquipoElectrico, Insumo } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { MobileFormSheet } from '../components/MobileFormSheet';
import { useIsMobile } from '../lib/useMediaQuery';

type FormInsumo = { id?: string; nombre: string; unidad: string; precioOverride: string; nota: string; categoriaId: string; conStock: boolean };
const empty: FormInsumo = { nombre: '', unidad: '', precioOverride: '', nota: '', categoriaId: '', conStock: true };

type CalcState = {
  insumoId: string;
  precioUnitario: string;
  precioTotal: string;
  cantidad: string;
};
const calcEmpty: CalcState = { insumoId: '', precioUnitario: '', precioTotal: '', cantidad: '' };

type FormEquipo = { id?: string; nombre: string; potenciaW: string; activo: boolean; nota: string };
const equipoEmpty: FormEquipo = { nombre: '', potenciaW: '', activo: true, nota: '' };

function toNumEs(v: string): number {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export function InsumosPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [form, setForm] = useState<FormInsumo>(empty);
  const [calc, setCalc] = useState<CalcState>(calcEmpty);
  const [mensajeCalc, setMensajeCalc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheetInsumoOpen, setSheetInsumoOpen] = useState(false);
  const [sheetEquipoOpen, setSheetEquipoOpen] = useState(false);

  const q = useQuery({
    queryKey: ['insumos'],
    queryFn: () => API.get<Insumo[]>('/insumos'),
    enabled: tiene('insumos:read'),
  });

  const categoriasQ = useQuery({
    queryKey: ['categorias'],
    queryFn: () => API.get<Categoria[]>('/categorias'),
    enabled: tiene('categorias:read'),
  });

  const categoriasGasto = (categoriasQ.data ?? []).filter(
    (c) => !c.padreId && (!c.tipoMovimientoPermitido || c.tipoMovimientoPermitido === 'gasto' || c.tipoMovimientoPermitido === 'compra'),
  );

  const guardar = useMutation({
    mutationFn: (body: FormInsumo) => {
      const payload = {
        nombre: body.nombre,
        unidad: body.unidad,
        precioOverride: body.precioOverride ? Number(body.precioOverride.replace(',', '.')) : null,
        nota: body.nota || null,
        categoriaId: body.categoriaId || null,
        conStock: body.conStock,
      };
      return body.id
        ? API.put<Insumo>(`/insumos/${body.id}`, payload)
        : API.post<Insumo>('/insumos', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insumos'] });
      setForm(empty);
      setError(null);
      setSheetInsumoOpen(false);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => API.del(`/insumos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insumos'] }),
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const aplicarPrecio = useMutation({
    mutationFn: ({ id, precioOverride }: { id: string; precioOverride: number }) =>
      API.put<Insumo>(`/insumos/${id}`, { precioOverride }),
    onSuccess: (ins) => {
      qc.invalidateQueries({ queryKey: ['insumos'] });
      setMensajeCalc(`✓ Precio de "${ins.nombre}" actualizado a ${cop(Number(ins.precioOverride))}/${ins.unidad}.`);
    },
    onError: (err) => setMensajeCalc((err as unknown as ApiError).message ?? 'Error'),
  });

  const [formEquipo, setFormEquipo] = useState<FormEquipo>(equipoEmpty);
  const [errorEquipo, setErrorEquipo] = useState<string | null>(null);

  const qEquipos = useQuery({
    queryKey: ['equipos'],
    queryFn: () => API.get<EquipoElectrico[]>('/equipos'),
    enabled: tiene('equipos:read'),
  });

  const guardarEquipo = useMutation({
    mutationFn: (body: FormEquipo) => {
      const payload = {
        nombre: body.nombre,
        potenciaW: Number(body.potenciaW.replace(',', '.')),
        activo: body.activo,
        nota: body.nota || null,
      };
      return body.id
        ? API.put<EquipoElectrico>(`/equipos/${body.id}`, payload)
        : API.post<EquipoElectrico>('/equipos', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['equipos'] });
      setFormEquipo(equipoEmpty);
      setErrorEquipo(null);
      setSheetEquipoOpen(false);
    },
    onError: (err) => setErrorEquipo((err as unknown as ApiError).message ?? 'Error'),
  });

  const eliminarEquipo = useMutation({
    mutationFn: (id: string) => API.del(`/equipos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipos'] }),
    onError: (err) => setErrorEquipo((err as unknown as ApiError).message ?? 'Error'),
  });

  function submitEquipo(e: React.FormEvent) {
    e.preventDefault();
    if (!formEquipo.nombre.trim() || !formEquipo.potenciaW.trim()) {
      setErrorEquipo('Nombre y potencia son obligatorios');
      return;
    }
    guardarEquipo.mutate(formEquipo);
  }

  const insumoCalc = q.data?.find((i) => i.id === calc.insumoId);
  const esLuz = insumoCalc?.nombre === 'Luz' && insumoCalc?.unidad === 'kWh';
  const precioUnitario = esLuz
    ? toNumEs(calc.precioUnitario)
    : (() => {
        const precioTotal = toNumEs(calc.precioTotal);
        const cantidad = toNumEs(calc.cantidad);
        return cantidad > 0 ? precioTotal / cantidad : 0;
      })();

  function exportarXlsx() {
    const items = q.data ?? [];
    if (items.length === 0) return;
    downloadXlsx(fileNameConFecha('insumos'), [
      {
        name: 'Insumos',
        rows: items,
        columns: [
          { header: 'Nombre', value: (i) => i.nombre, width: 22 },
          { header: 'Unidad', value: (i) => i.unidad, width: 8 },
          { header: 'Precio actual', value: (i) => i.precioActual ?? null, width: 14, numFmt: FMT_COP },
          {
            header: 'Override manual',
            value: (i) => (i.precioOverride !== null && i.precioOverride !== undefined ? Number(i.precioOverride) : null),
            width: 14,
            numFmt: FMT_COP,
          },
          { header: 'Nota', value: (i) => i.nota ?? '', width: 30 },
        ],
      },
    ]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim() || !form.unidad.trim()) {
      setError('Nombre y unidad son obligatorios');
      return;
    }
    guardar.mutate(form);
  }

  function openEditInsumo(i: Insumo) {
    setForm({
      id: i.id,
      nombre: i.nombre,
      unidad: i.unidad,
      precioOverride: i.precioOverride ?? '',
      nota: i.nota ?? '',
      categoriaId: i.categoriaId ?? '',
      conStock: i.conStock,
    });
    if (isMobile) setSheetInsumoOpen(true);
  }

  function openEditEquipo(e: EquipoElectrico) {
    setFormEquipo({
      id: e.id,
      nombre: e.nombre,
      potenciaW: String(e.potenciaW),
      activo: e.activo,
      nota: e.nota ?? '',
    });
    if (isMobile) setSheetEquipoOpen(true);
  }

  const insumoColumns: Column<Insumo>[] = [
    { key: 'nombre', header: 'Insumo', mobile: 'primary', render: (i) => <span className="font-medium">{i.nombre}</span> },
    {
      key: 'precio',
      header: 'Precio actual',
      mobile: 'secondary',
      align: 'right',
      render: (i) => (
        <span>
          {cop(i.precioActual)}<span className="text-slate-400 text-xs"> /{i.unidad}</span>
        </span>
      ),
    },
    { key: 'unidad', header: 'Unidad', render: (i) => i.unidad },
    {
      key: 'categoria',
      header: 'Categoría',
      render: (i) =>
        i.categoria?.nombre ? (
          <span className="pill bg-slate-100 text-slate-700">{i.categoria.nombre}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'stock',
      header: 'Stock',
      align: 'right',
      render: (i) => {
        if (!i.conStock) {
          return (
            <span
              className="text-xs text-slate-400 italic"
              title="Los servicios (agua, luz, mano de obra) se pagan por consumo y no acumulan stock. Solo se registra su precio unitario para el cálculo de producción."
            >
              No se calcula
            </span>
          );
        }
        const s = i.stockActual ?? 0;
        const color =
          s > 0 ? 'text-emerald-700' : s < 0 ? 'text-red-600' : 'text-slate-500';
        return (
          <span className={`${color} font-medium`}>
            {num(s, 2)} <span className="text-slate-400 text-xs">{i.unidad}</span>
          </span>
        );
      },
    },
    {
      key: 'override',
      header: 'Override',
      render: (i) => (i.precioOverride ? <span className="pill bg-amber-50 text-amber-700">{cop(Number(i.precioOverride))}</span> : '—'),
    },
    { key: 'nota', header: 'Nota', render: (i) => i.nota ?? '—' },
  ];

  const equipoColumns: Column<EquipoElectrico>[] = [
    { key: 'nombre', header: 'Equipo', mobile: 'primary', render: (e) => <span className="font-medium">{e.nombre}</span> },
    { key: 'pot', header: 'Potencia', mobile: 'secondary', align: 'right', render: (e) => `${Number(e.potenciaW)} W` },
    {
      key: 'estado',
      header: 'Estado',
      render: (e) =>
        e.activo
          ? <span className="pill bg-emerald-50 text-emerald-700">Activo</span>
          : <span className="pill bg-slate-100 text-slate-500">Inactivo</span>,
    },
    { key: 'nota', header: 'Nota', render: (e) => e.nota ?? '—' },
  ];

  const formInsumoJsx = (
    <form className="space-y-3" onSubmit={submit}>
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} required />
      </div>
      <div>
        <label className="label">Unidad</label>
        <input className="input" value={form.unidad} onChange={(e) => setForm((s) => ({ ...s, unidad: e.target.value }))} placeholder="kg, lb, und, L, h" required />
      </div>
      <div>
        <label className="label">Categoría contable</label>
        <select
          className="input"
          value={form.categoriaId}
          onChange={(e) => setForm((s) => ({ ...s, categoriaId: e.target.value }))}
        >
          <option value="">— Sin categoría —</option>
          {categoriasGasto.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        <p className="text-[11px] text-slate-500 mt-1">
          Al registrar una compra de este insumo, se clasificará automáticamente en esta categoría.
        </p>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={form.conStock}
          onChange={(e) => setForm((s) => ({ ...s, conStock: e.target.checked }))}
        />
        <span>
          Lleva stock
          <span className="block text-[11px] text-slate-500">
            Desmarcalo para servicios (agua, luz, mano de obra) — se siguen usando para calcular el costo del lote, pero sin acumular stock.
          </span>
        </span>
      </label>
      <div>
        <label className="label">Precio override (COP)</label>
        <input type="number" step="any" inputMode="decimal" className="input" value={form.precioOverride} onChange={(e) => setForm((s) => ({ ...s, precioOverride: e.target.value }))} placeholder="Dejar vacío para usar último gasto" />
      </div>
      <div>
        <label className="label">Nota</label>
        <input className="input" value={form.nota} onChange={(e) => setForm((s) => ({ ...s, nota: e.target.value }))} />
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1" disabled={guardar.isPending}>
          {form.id ? 'Guardar cambios' : 'Crear insumo'}
        </button>
        {form.id && (
          <button type="button" className="btn-secondary" onClick={() => { setForm(empty); setError(null); }}>
            Cancelar
          </button>
        )}
      </div>
    </form>
  );

  const formEquipoJsx = (
    <form className="space-y-3" onSubmit={submitEquipo}>
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={formEquipo.nombre} onChange={(e) => setFormEquipo((s) => ({ ...s, nombre: e.target.value }))} placeholder="Motor, Ventilador..." required />
      </div>
      <div>
        <label className="label">Potencia (W)</label>
        <input type="number" step="any" min="0" inputMode="decimal" className="input" value={formEquipo.potenciaW} onChange={(e) => setFormEquipo((s) => ({ ...s, potenciaW: e.target.value }))} placeholder="186.5" required />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={formEquipo.activo} onChange={(e) => setFormEquipo((s) => ({ ...s, activo: e.target.checked }))} />
        Activo (disponible en la calculadora)
      </label>
      <div>
        <label className="label">Nota</label>
        <input className="input" value={formEquipo.nota} onChange={(e) => setFormEquipo((s) => ({ ...s, nota: e.target.value }))} />
      </div>
      {errorEquipo && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{errorEquipo}</div>}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1" disabled={guardarEquipo.isPending}>
          {formEquipo.id ? 'Guardar cambios' : 'Crear equipo'}
        </button>
        {formEquipo.id && (
          <button type="button" className="btn-secondary" onClick={() => { setFormEquipo(equipoEmpty); setErrorEquipo(null); }}>
            Cancelar
          </button>
        )}
      </div>
    </form>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Insumos"
        subtitle="Los precios se calculan automáticamente desde el último gasto de cada insumo. Override manual si lo necesitas."
        action={
          <button
            type="button"
            className="btn-secondary"
            onClick={exportarXlsx}
            disabled={!q.data?.length}
          >
            ⬇ .xlsx
          </button>
        }
      />

      {tiene('insumos:write') && (
        <div className="card p-4 sm:p-5 space-y-4">
          <div>
            <h2 className="font-semibold">Calculadora de precio unitario</h2>
            <p className="text-xs text-slate-500 mt-1">
              Para la luz ingresa directamente el valor por 1 kWh. En los demás insumos se calcula desde precio total ÷ cantidad. Se guarda como override del insumo.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="label">Insumo</label>
              <select
                className="input"
                value={calc.insumoId}
                onChange={(e) => {
                  setCalc((s) => ({ ...s, insumoId: e.target.value }));
                  setMensajeCalc(null);
                }}
              >
                <option value="">— Elegir —</option>
                {q.data?.map((i) => (
                  <option key={i.id} value={i.id}>{i.nombre} ({i.unidad})</option>
                ))}
              </select>
            </div>
            <div>
              {esLuz ? (
                <>
                  <label className="label">Precio por 1 kWh (COP)</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    inputMode="decimal"
                    className="input"
                    value={calc.precioUnitario}
                    onChange={(e) => { setCalc((s) => ({ ...s, precioUnitario: e.target.value })); setMensajeCalc(null); }}
                    placeholder="801"
                  />
                </>
              ) : (
                <>
                  <label className="label">Precio total (COP)</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    inputMode="decimal"
                    className="input"
                    value={calc.precioTotal}
                    onChange={(e) => { setCalc((s) => ({ ...s, precioTotal: e.target.value })); setMensajeCalc(null); }}
                    placeholder="2500"
                  />
                </>
              )}
            </div>
            <div>
              {esLuz ? (
                <div className="rounded-md p-3 border bg-slate-50 border-slate-200 h-full flex flex-col justify-center">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Referencia</div>
                  <div className="text-sm font-medium mt-0.5">
                    801 significa 801 COP por 1 kWh.
                  </div>
                </div>
              ) : (
                <>
                  <label className="label">Cantidad {insumoCalc && <span className="text-slate-400 font-normal">({insumoCalc.unidad})</span>}</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    inputMode="decimal"
                    className="input"
                    value={calc.cantidad}
                    onChange={(e) => { setCalc((s) => ({ ...s, cantidad: e.target.value })); setMensajeCalc(null); }}
                    placeholder="100"
                  />
                </>
              )}
            </div>
            <div className="rounded-md p-3 border bg-slate-50 border-slate-200 sm:col-span-2 md:col-span-1">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Precio unitario</div>
              <div className="text-lg font-semibold mt-0.5">
                {precioUnitario > 0 ? cop(precioUnitario) : '—'}
                {insumoCalc && precioUnitario > 0 && <span className="text-slate-400 text-xs font-normal"> /{insumoCalc.unidad}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              disabled={!calc.insumoId || precioUnitario <= 0 || aplicarPrecio.isPending}
              onClick={() => aplicarPrecio.mutate({ id: calc.insumoId, precioOverride: precioUnitario })}
            >
              {aplicarPrecio.isPending ? 'Guardando…' : 'Aplicar como override'}
            </button>
            {(calc.insumoId || calc.precioUnitario || calc.precioTotal || calc.cantidad) && (
              <button
                type="button"
                className="btn-secondary w-full sm:w-auto"
                onClick={() => { setCalc(calcEmpty); setMensajeCalc(null); }}
              >
                Limpiar
              </button>
            )}
            {mensajeCalc && (
              <div className={`text-sm ${mensajeCalc.startsWith('✓') ? 'text-emerald-700' : 'text-red-600'}`}>
                {mensajeCalc}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {tiene('insumos:write') && !isMobile && (
          <div className="card p-5 space-y-3 lg:col-span-1">
            <h2 className="font-semibold">{form.id ? 'Editar insumo' : 'Nuevo insumo'}</h2>
            {formInsumoJsx}
          </div>
        )}

        <div className={`card ${tiene('insumos:write') && !isMobile ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          <ResponsiveTable<Insumo>
            columns={insumoColumns}
            rows={q.data}
            rowKey={(i) => i.id}
            loading={q.isLoading}
            empty="Sin insumos."
            actions={(i) =>
              tiene('insumos:write') ? (
                <>
                  <button type="button" className="btn-secondary text-xs" onClick={() => openEditInsumo(i)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className="btn-danger text-xs"
                    onClick={() => { if (confirm(`¿Eliminar "${i.nombre}"?`)) eliminar.mutate(i.id); }}
                  >
                    Eliminar
                  </button>
                </>
              ) : null
            }
          />
        </div>
      </div>

      {tiene('insumos:write') && (
        <>
          <button
            type="button"
            onClick={() => { setForm(empty); setError(null); setSheetInsumoOpen(true); }}
            className="md:hidden fab"
            aria-label="Nuevo insumo"
          >
            +
          </button>
          <MobileFormSheet
            open={sheetInsumoOpen}
            onClose={() => setSheetInsumoOpen(false)}
            title={form.id ? 'Editar insumo' : 'Nuevo insumo'}
          >
            {formInsumoJsx}
          </MobileFormSheet>
        </>
      )}

      {tiene('equipos:read') && (
        <div className="space-y-3">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold">Equipos eléctricos</h2>
            <p className="text-sm text-slate-500">
              Registrá cada equipo con su potencia (W). Los equipos <b>activos</b> aparecen en la calculadora de producción.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {tiene('equipos:write') && !isMobile && (
              <div className="card p-5 space-y-3 lg:col-span-1">
                <h3 className="font-semibold">{formEquipo.id ? 'Editar equipo' : 'Nuevo equipo'}</h3>
                {formEquipoJsx}
              </div>
            )}

            <div className={`card ${tiene('equipos:write') && !isMobile ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              <ResponsiveTable<EquipoElectrico>
                columns={equipoColumns}
                rows={qEquipos.data}
                rowKey={(e) => e.id}
                loading={qEquipos.isLoading}
                empty="Sin equipos registrados."
                actions={(e) =>
                  tiene('equipos:write') ? (
                    <>
                      <button type="button" className="btn-secondary text-xs" onClick={() => openEditEquipo(e)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-danger text-xs"
                        onClick={() => { if (confirm(`¿Eliminar "${e.nombre}"?`)) eliminarEquipo.mutate(e.id); }}
                      >
                        Eliminar
                      </button>
                    </>
                  ) : null
                }
              />
            </div>
          </div>

          {tiene('equipos:write') && (
            <MobileFormSheet
              open={sheetEquipoOpen}
              onClose={() => setSheetEquipoOpen(false)}
              title={formEquipo.id ? 'Editar equipo' : 'Nuevo equipo'}
            >
              {formEquipoJsx}
            </MobileFormSheet>
          )}

          {tiene('equipos:write') && (
            <button
              type="button"
              onClick={() => { setFormEquipo(equipoEmpty); setErrorEquipo(null); setSheetEquipoOpen(true); }}
              className="md:hidden w-full btn-secondary"
            >
              + Nuevo equipo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
